import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shuffleArray } from '../../../lib/utils';
import RandomBubble from './RandomBubble';

const CHANNELS = [60, 120, 180, 240];
const BUBBLE_SIZE = 100;

function getNextBubbleChannel(lastBubbleChannel) {
  const randomSeed = Math.random() <= 0.5;

  if (lastBubbleChannel > (CHANNELS.length - 1) / 2) {
    // last bubble was in the lower half, pick an upper half track
    return randomSeed ? 0 : 1;
  }

  // last bubble was in the upper half, pick a lower half track
  return randomSeed ? 2 : 3;
}

function getNextBubbleCoordinates(lastChannel, isInitialState) {
  let bubbleChannel;

  // initial state creates a random set of bubbles (with a channel)
  // since lastchannel is being updated in the component, this guarantees
  // the randomness of the initial set of bubbles being displayed
  if (isInitialState) {
    bubbleChannel = Math.random() * Math.max(4);
  } else {
    bubbleChannel = lastChannel;
  }

  const channel = getNextBubbleChannel(bubbleChannel);

  const x = -110;
  const y = CHANNELS[channel] - 50;

  return { x, y, channel };
}

const bubbleInBounds = ({ x, y }, ref) => {
  const {
    clientWidth,
    clientHeight,
  } = ref.current;

  return x < clientWidth + 10 && y < clientHeight - BUBBLE_SIZE;
};

function useAnimation(callback) {
  const frame = useRef();

  const animate = useCallback(() => {
    callback();
    frame.current = window.requestAnimationFrame(animate);
  }, [callback]);

  useEffect(() => {
    animate();

    return () => {
      if (frame.current) {
        window.cancelAnimationFrame(frame.current);
      }
    };
  }, [animate]);
}

function useShuffledArray(items) {
  return useMemo(() => shuffleArray(items), [items]);
}

export function useBubbles(brands) {
  const shuffledBrands = useShuffledArray(brands);
  const containerRef = useRef();
  const lastChannel = useRef(0);

  const generateBubble = (brand, index) => {
    const options = {
      ...brand,
      ...getNextBubbleCoordinates(lastChannel.current, true),
      x: index * 125,
    };

    const newBubble = new RandomBubble(options);

    lastChannel.current = newBubble.channel;

    return newBubble;
  };

  const isVisible = bubble => !containerRef.current || bubbleInBounds(bubble, containerRef);
  const isNotVisible = bubble => containerRef.current && !bubbleInBounds(bubble, containerRef);
  const [bubbles, setBubbles] = useState(shuffledBrands.map(generateBubble).filter(isVisible));

  const animate = useCallback(() => {
    setBubbles((prev) => {
      const prevVisible = prev.filter(isVisible);
      const next = prev.map(bubble => bubble.next());
      const nextVisible = next.filter(isVisible);

      if (nextVisible.length < prevVisible.length) {
        shuffleArray(next.filter(isNotVisible))[0].respawn();
      }

      return next;
    });
  }, []);

  useAnimation(animate);

  useEffect(() => {
    setBubbles(shuffledBrands.map(generateBubble).filter(isVisible));
  }, [shuffledBrands]);

  return { bubbles, containerRef };
}
